/**
 * LocaleLayout: wraps all routes for a given locale.
 *
 * This component is the critical piece for fixing the SSG bug. For each locale
 * route (/en/... or /ar/...), LocaleLayout:
 *
 * 1. Derives locale from pathname (localeFromPathname)
 * 2. Creates a cloned i18n instance for that locale (via i18n.cloneInstance)
 * 3. Passes it through I18nextProvider so useTranslation() gets the right language
 * 4. Wraps everything in LanguageProvider so navigation can switch locales
 * 5. Renders the nested route content
 *
 * The cloned instances are kept at module scope so they're reused, not rebuilt
 * per page. This way SSG can emit both /en/index.html (lang="en") and
 * /ar/index.html (lang="ar") with correct text in each.
 */

import { ReactNode } from "react";
import { Outlet } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import i18n from "@/lib/i18n";
import { localeFromPathname, type Locale } from "@/lib/locale";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Layout from "@/components/Layout";

// Create cloned instances once per locale, so they're reused across all pages.
// This is critical: rebuilding per page would cause SSG to emit the same
// prerendered HTML for every locale (the bug we're fixing).
const i18nInstances: Record<Locale, typeof i18n> = {
  en: i18n.cloneInstance({ lng: "en" }),
  ar: i18n.cloneInstance({ lng: "ar" }),
};

export function LocaleLayout({ locale }: { locale: Locale }) {
  const instance = i18nInstances[locale];

  return (
    <I18nextProvider i18n={instance}>
      <LanguageProvider language={locale} i18n={instance}>
        <Layout>
          <Outlet />
        </Layout>
      </LanguageProvider>
    </I18nextProvider>
  );
}
